// A design's languages (internal/layout/languages.go): the tabs under the editor's
// canvas, what a kiosk offers, and what a display shows or rotates through.
import rules from './designLanguages.json'
import { needsWords } from '../lib/i18n/missing'
import type { LayoutDocument, LayoutKind, LayoutText } from './types'

export const MIN_ROTATE_SEC: number = rules.minRotateSec
export const MAX_ROTATE_SEC: number = rules.maxRotateSec
export const DEFAULT_ROTATE_SEC: number = rules.defaultRotateSec
export const ROTATE_CHOICES: readonly number[] = rules.rotateChoices

/** Whether a design of this kind may change language on a timer. */
export const rotates = (kind: LayoutKind): boolean => kind === 'display' || kind === 'counter'

type Words = Pick<LayoutDocument, 'elements' | 'called' | 'words' | 'languages'>

function hasWordsIn(doc: Words, lang: string): boolean {
  const written = (t: LayoutText | undefined) => needsWords(t?.[lang])
  // A stored draft may carry the Called screen's elements packed (calledScreen.ts).
  for (const el of [...doc.elements, ...(doc.called?.elements ?? [])]) {
    if (written(el.content) || written(el.subtitle)) return true
  }
  return Object.values(doc.words ?? {}).some(written)
}

/**
 * The design's languages among the organisation's customer languages, in the
 * design's order. A design that names none has every customer language it holds
 * words in, the default first (Document.LanguagesFor). Never empty.
 */
export function designLanguages(doc: Words, customer: readonly string[]): string[] {
  const offered = customer.length > 0 ? customer : ['en']
  const out: string[] = []
  if (doc.languages && doc.languages.length > 0) {
    for (const c of doc.languages) if (offered.includes(c) && !out.includes(c)) out.push(c)
  } else {
    offered.forEach((c, i) => {
      if (i === 0 || hasWordsIn(doc, c)) out.push(c)
    })
  }
  return out.length > 0 ? out : [offered[0]!]
}

/** The document naming exactly these languages. */
export function withLanguages(doc: LayoutDocument, languages: readonly string[]): LayoutDocument {
  return { ...doc, languages: [...languages] }
}

/** A tab added at the end; one already there is left where it is. */
export function addLanguage(doc: LayoutDocument, customer: readonly string[], code: string): LayoutDocument {
  const now = designLanguages(doc, customer)
  return withLanguages(doc, now.includes(code) ? now : [...now, code])
}

/** A tab removed; its words stay in the document. The last one stays. */
export function removeLanguage(doc: LayoutDocument, customer: readonly string[], code: string): LayoutDocument {
  const now = designLanguages(doc, customer)
  const next = now.filter((c) => c !== code)
  return next.length > 0 ? withLanguages(doc, next) : doc
}

/** A tab moved one place left (-1) or right (+1). */
export function moveLanguage(doc: LayoutDocument, customer: readonly string[], code: string, by: -1 | 1): LayoutDocument {
  const now = designLanguages(doc, customer)
  const from = now.indexOf(code)
  const to = from + by
  if (from < 0 || to < 0 || to >= now.length) return doc
  const next = [...now]
  next.splice(from, 1)
  next.splice(to, 0, code)
  return withLanguages(doc, next)
}

/** One language on screen (0), or each in turn every `seconds`. */
export function withRotation(doc: LayoutDocument, seconds: number): LayoutDocument {
  const next = { ...doc }
  if (seconds > 0 && rotates(doc.kind)) next.rotateSec = seconds
  else delete next.rotateSec
  return next
}

/** What a screen shows: its languages and seconds each (0 = the first only). */
export interface ScreenLanguages {
  languages: string[]
  rotateSec: number
}

/**
 * A display's languages: the design's, rotating as it says, unless the machine is
 * pinned to one of them (a counter display's own Language).
 */
export function screenLanguages(doc: Words & Pick<LayoutDocument, 'kind' | 'rotateSec'>, offered: readonly string[], pinned?: string): ScreenLanguages {
  const languages = designLanguages(doc, offered)
  if (pinned && languages.includes(pinned)) return { languages: [pinned], rotateSec: 0 }
  const rotateSec = rotates(doc.kind) && languages.length > 1 ? (doc.rotateSec ?? 0) : 0
  return { languages, rotateSec }
}
